import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { WhatsappAssignmentApiService } from '../services/whatsapp-assignment-api.service';
import { WhatsappConversationApiService } from '../services/whatsapp-conversation-api.service';
import { WhatsappMessageApiService } from '../services/whatsapp-message-api.service';
import { Observable, of, switchMap } from 'rxjs';
import {
  SendMessageRequest,
  TemplateHeaderFields,
  WhatsappConversation,
  WhatsappMessageResponse,
  WhatsappPhoneResponse,
  WhatsappSendableMediaType,
  WhatsappTemplate,
  isTemplateSendable,
  isWhatsappMediaMessage,
  requiresMediaHeader,
  WHATSAPP_MESSAGE_TYPE_LABELS,
  whatsappLabel,
} from '../models/whatsapp.model';
import { normalizeForComparison } from '../../../shared/utils/text-normalization.utils';
import { ToastService } from '../../../shared/services/toast.service';
import { ContactoDrawerService } from '../../contactos/services/contacto-drawer.service';

@Component({
  selector: 'app-whatsapp-inbox',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, RouterLink],
  templateUrl: './whatsapp-inbox.component.html',
})
export class WhatsappInboxComponent implements OnInit, OnDestroy, AfterViewChecked {
  private readonly assignmentApi = inject(WhatsappAssignmentApiService);
  private readonly conversationApi = inject(WhatsappConversationApiService);
  private readonly messageApi = inject(WhatsappMessageApiService);
  private readonly toast = inject(ToastService);
  private readonly contactoDrawer = inject(ContactoDrawerService);

  loadingPhones = true;
  loadingConversations = false;
  loadingMessages = false;
  sending = false;
  errorMessage: string | null = null;

  phones: WhatsappPhoneResponse[] = [];
  selectedPhoneId: number | null = null;

  conversations: WhatsappConversation[] = [];
  /** Conversaciones del número elegido que pasan el buscador: lo que pinta la lista. */
  conversacionesVisibles: WhatsappConversation[] = [];
  /** Texto del buscador. Sobrevive a los ticks del sondeo y a cambiar de número. */
  busqueda = '';
  selectedConversation: WhatsappConversation | null = null;

  messages: WhatsappMessageResponse[] = [];
  composerText = '';

  /** Adjunto del mensaje libre. Se sube a Meta justo antes de enviar. */
  attachment: File | null = null;
  attachmentPreviewUrl: string | null = null;

  /**
   * Object URLs de los adjuntos ya descargados, por id de mensaje. El adjunto no se puede
   * poner directo en un `src`: el endpoint exige el bearer token, asi que hay que bajarlo
   * con HttpClient (que si pasa por el interceptor) y envolverlo en un blob.
   */
  private readonly mediaUrls = new Map<number, string>();

  /** Plantillas: sólo se usan cuando la ventana de 24h está cerrada. */
  templates: WhatsappTemplate[] = [];
  templatesLoading = false;
  templatesLoaded = false;
  /**
   * Motivo por el que no se pudieron traer. Con el token de Meta caducado la lista llega
   * vacia por un 409, no porque el negocio no tenga plantillas: sin esto la pantalla
   * mandaba a crear una plantilla que no iba a poder crearse.
   */
  templatesError: string | null = null;
  selectedTemplateName = '';
  templateParams: string[] = [];
  /** Archivo de la cabecera IMAGE/VIDEO/DOCUMENT. Se sube a Meta justo antes de enviar. */
  headerFile: File | null = null;
  /** Valor del {{1}} de una cabecera de texto. */
  headerParam = '';
  uploadingMedia = false;

  /**
   * Selector de «Mis números». Vive arriba como desplegable en vez de ocupar una columna
   * fija: la bandeja necesita el ancho para el hilo, y los números se cambian de tanto en
   * tanto, no en cada mensaje.
   */
  phonesOpen = false;
  @ViewChild('phonesMenu') phonesMenuRef?: ElementRef<HTMLElement>;

  /** Paleta de emojis del redactor. */
  emojiOpen = false;
  @ViewChild('emojiMenu') emojiMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('composerInput') composerInputRef?: ElementRef<HTMLTextAreaElement>;

  /**
   * Emojis ofrecidos, agrupados como en WhatsApp. Es una lista fija a propósito: una
   * librería de picker traería su propio CSS y un bundle mucho mayor que esto.
   */
  readonly emojiGroups: { label: string; emojis: string[] }[] = [
    {
      label: 'Caritas',
      emojis: [
        '😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩',
        '😘','😗','😚','😙','😋','😛','🤪','🤗','🤔','🤨','😐','😑','😶','🙄','😏','😴',
        '😪','😥','😢','😭','😤','😠','😡','🥺','😳','🤯','😬','😰','🤒','🤕','🤧','😷',
      ],
    },
    {
      label: 'Gestos',
      emojis: [
        '👍','👎','👌','🤝','👏','🙌','🙏','💪','✍️','👋','🤙','✌️','🤞','☝️','👇','👈',
        '👉','🫡','🤦','🤷','💁','🙋','🧑‍💻','👀',
      ],
    },
    {
      label: 'Objetos y símbolos',
      emojis: [
        '❤️','🧡','💛','💚','💙','💜','🖤','💯','✅','❌','⚠️','❗','❓','⭐','🔥','🎉',
        '🎊','🎁','📌','📎','📅','📞','📱','💬','📢','💡','💰','💳','🧾','📦','🚀','⏰',
        '⌛','🕒','📍','🏠','🏢','🛒','☕','✈️',
      ],
    },
  ];

  /** Hilo: se baja al último mensaje en cuanto el DOM esté pintado. */
  @ViewChild('messagesScroll') messagesScrollRef?: ElementRef<HTMLElement>;
  private pendingScrollToBottom = false;

  /**
   * Alta manual de la ficha del hilo abierto.
   *
   * Con el alta automática apagada (que es el valor por defecto), la mayoría de las
   * conversaciones llegan sin `contactId`: la ficha se crea sólo cuando el asesor decide
   * que esa persona lo merece. El nombre es opcional — sin él, el backend la deja marcada
   * y la rellena con el perfil que mande Meta en el siguiente entrante.
   */
  crearContactoAbierto = false;
  nombreNuevoContacto = '';
  creandoContacto = false;

  /** Iniciar conversación con alguien que nunca escribió. */
  startOpen = false;
  startPhoneId: number | null = null;
  startTo = '';

  /**
   * Autorefresco de la bandeja.
   *
   * Sin esto un mensaje entrante no aparece nunca mientras el asesor mira el chat: los
   * mensajes solo se cargaban al pulsar una conversacion. Se sondea en vez de abrir un
   * websocket porque es el mismo patron que ya usa el resto del CRM y no exige nada del
   * backend.
   *
   * Se salta el tick cuando la pestana esta oculta: Chrome estrangula los timers en
   * segundo plano, y ademas refrescar lo que nadie mira solo gasta peticiones.
   */
  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private static readonly POLL_MS = 15000;

  ngOnInit(): void {
    this.loadPhones();
    this.pollHandle = setInterval(() => this.pollTick(), WhatsappInboxComponent.POLL_MS);
  }

  ngAfterViewChecked(): void {
    if (!this.pendingScrollToBottom) return;
    const el = this.scrollElement();
    if (!el) return;
    this.pendingScrollToBottom = false;
    el.scrollTop = el.scrollHeight;
  }

  ngOnDestroy(): void {
    if (this.pollHandle) clearInterval(this.pollHandle);
    // Las object URLs no se liberan solas: sin esto la pestana acumula los blobs de
    // cada adjunto que se abrio durante la sesion.
    this.mediaUrls.forEach((url) => URL.revokeObjectURL(url));
    this.mediaUrls.clear();
    this.clearAttachment();
  }

  private pollTick(): void {
    if (document.hidden) return;
    // Nada de spinners ni de borrar el error visible: un refresco de fondo que parpadea
    // es peor que no refrescar.
    if (this.sending || this.loadingMessages || this.loadingConversations) return;

    this.conversationApi.list(50, 0).subscribe({
      next: (list) => {
        this.conversations = list || [];
        this.aplicarBusqueda();
        this.syncSelectedConversation();
      },
      error: () => {
        /* Silencioso a proposito: el tick no debe pintar errores encima del chat. */
      },
    });

    const conv = this.selectedConversation;
    if (!conv) return;
    this.conversationApi.listMessages(conv.id).subscribe({
      next: (list) => this.applyMessages(list, false),
      error: () => {
        /* idem */
      },
    });
  }

  loadPhones(): void {
    this.loadingPhones = true;
    this.errorMessage = null;
    this.assignmentApi.myPhones().subscribe({
      next: (phones) => {
        this.phones = phones || [];
        this.loadingPhones = false;
        if (this.phones.length > 0 && !this.selectedPhoneId) {
          this.selectedPhoneId = this.phones[0].id;
        }
        if (this.phones.length > 0) this.loadConversations();
      },
      error: (err) => {
        this.errorMessage = this.toErrorMessage(err, 'No se pudieron cargar tus números');
        this.loadingPhones = false;
      },
    });
  }

  loadConversations(): void {
    this.loadingConversations = true;
    this.errorMessage = null;
    this.conversationApi.list(50, 0).subscribe({
      next: (list) => {
        this.conversations = list || [];
        this.aplicarBusqueda();
        this.loadingConversations = false;
        // Si la conversación activa ya no está, deseleccionar
        if (
          this.selectedConversation &&
          !this.conversations.some((c) => c.id === this.selectedConversation!.id)
        ) {
          this.selectedConversation = null;
          this.messages = [];
        }
        this.syncSelectedConversation();
      },
      error: (err) => {
        this.errorMessage = this.toErrorMessage(err, 'No se pudieron cargar las conversaciones');
        this.loadingConversations = false;
      },
    });
  }

  /**
   * Vuelve a apuntar la conversación abierta al objeto recién traído del backend.
   *
   * `selectedConversation` guardaba la instancia del momento del clic, y tanto el sondeo
   * como {@link loadConversations} reemplazan la lista por objetos nuevos: la
   * `customerWindowUntil` que lee el composer se quedaba congelada. En una conversación
   * nacida de «Iniciar conversación» (ventana null) el cliente respondía, su mensaje se
   * pintaba en el chat — el sondeo sí traía los mensajes — y el composer seguía exigiendo
   * plantilla hasta volver a hacer clic en la conversación o recargar la página. Pasaba
   * igual al revés: la ventana vencía con el chat abierto, la pantalla seguía ofreciendo
   * texto libre y el envío moría con el 409 del backend.
   */
  private syncSelectedConversation(): void {
    const current = this.selectedConversation;
    if (!current) return;
    const fresh = this.conversations.find((c) => c.id === current.id);
    if (!fresh) return;
    this.selectedConversation = fresh;
    // La ventana pudo cerrarse con el chat abierto y las plantillas sólo se piden al
    // seleccionar: sin esto no habría catálogo que ofrecer. `templatesError` corta el
    // reintento en cada tick cuando el catálogo de Meta está fallando; el botón
    // «Reintentar» lo limpia y vuelve a entrar.
    if (!this.isWindowOpen(fresh) && !this.templatesError) this.loadTemplates();
  }

  get conversationsForSelectedPhone(): WhatsappConversation[] {
    if (!this.selectedPhoneId) return [];
    return this.conversations.filter((c) => c.whatsappPhoneId === this.selectedPhoneId);
  }

  buscar(texto: string): void {
    this.busqueda = texto;
    this.aplicarBusqueda();
  }

  limpiarBusqueda(): void {
    if (!this.busqueda) return;
    this.busqueda = '';
    this.aplicarBusqueda();
  }

  /**
   * Recalcula la lista visible: las del número elegido, filtradas por el buscador.
   *
   * Se hace aquí y no con un getter en la plantilla porque un getter que filtra se ejecuta
   * en CADA ciclo de detección de cambios, y esta lista se repinta cada 15 s por el sondeo.
   * Filtra en memoria a propósito: `GET /api/whatsapp/conversations` no acepta término de
   * búsqueda, y las 50 que trae ya están todas aquí.
   */
  private aplicarBusqueda(): void {
    const base = this.conversationsForSelectedPhone;
    const termino = this.normalizarBusqueda(this.busqueda);
    this.conversacionesVisibles = termino
      ? base.filter((c) => this.textoBuscable(c).includes(termino))
      : base;
  }

  /**
   * Lo que la fila enseña, en un solo texto: el nombre del contacto, su número y el relleno
   * que pinta la plantilla cuando falta alguno. El nombre entra desde que el backend lo
   * resuelve en el listado; antes solo se podía buscar por número.
   */
  private textoBuscable(conv: WhatsappConversation): string {
    return this.normalizarBusqueda(
      `${this.conversationTitle(conv)} ${this.conversationPhone(conv)} ${conv.id}`
    );
  }

  /**
   * Deja solo letras y dígitos. Un número se teclea de mil formas — `+51 933-820-255`,
   * `933 820`, `(51) 933` — y sin quitar el `+`, los espacios y los guiones de los DOS
   * lados, buscar tal y como está escrito en la pantalla no encontraba su propia fila.
   */
  private normalizarBusqueda(valor: string): string {
    return normalizeForComparison(valor).replace(/[^a-z0-9]/g, '');
  }

  selectPhone(id: number): void {
    this.phonesOpen = false;
    if (this.selectedPhoneId === id) return;
    this.selectedPhoneId = id;
    this.selectedConversation = null;
    this.messages = [];
    this.cerrarCrearContacto();
    this.aplicarBusqueda();
  }

  /** Número elegido, para pintarlo en el botón del desplegable. */
  get selectedPhone(): WhatsappPhoneResponse | null {
    return this.phones.find((p) => p.id === this.selectedPhoneId) ?? null;
  }

  togglePhones(): void {
    this.phonesOpen = !this.phonesOpen;
    if (this.phonesOpen) this.emojiOpen = false;
  }

  // ===== Emojis =====

  toggleEmoji(): void {
    this.emojiOpen = !this.emojiOpen;
    if (this.emojiOpen) this.phonesOpen = false;
  }

  /**
   * Inserta el emoji donde está el cursor (no al final): el asesor casi siempre lo mete en
   * mitad de una frase ya escrita. Deja el foco en el textarea y el cursor tras el emoji
   * para poder seguir tecleando.
   */
  insertEmoji(emoji: string): void {
    const textarea = this.composerInputRef?.nativeElement;
    if (!textarea) {
      this.composerText += emoji;
      return;
    }
    const start = textarea.selectionStart ?? this.composerText.length;
    const end = textarea.selectionEnd ?? start;
    this.composerText =
      this.composerText.slice(0, start) + emoji + this.composerText.slice(end);
    const cursor = start + emoji.length;
    // El valor del textarea lo escribe Angular en el siguiente ciclo: mover el cursor
    // antes lo dejaría en la posición vieja.
    queueMicrotask(() => {
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  }

  /**
   * Enter envía; Mayús+Enter (o Ctrl/Alt/Cmd+Enter) hace salto de línea.
   *
   * Se comprueba `isComposing` porque con teclado de acentos o IME el Enter que cierra la
   * composición llega como un keydown normal: sin esto, escribir «ñ» o un acento podía
   * mandar el mensaje a medias.
   */
  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;
    if (event.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    if (this.canSend) this.send();
  }

  /** Cierra los desplegables al pulsar fuera de ellos. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (this.phonesOpen && !this.phonesMenuRef?.nativeElement.contains(target)) {
      this.phonesOpen = false;
    }
    if (this.emojiOpen && !this.emojiMenuRef?.nativeElement.contains(target)) {
      this.emojiOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.phonesOpen = false;
    this.emojiOpen = false;
  }

  selectConversation(conv: WhatsappConversation): void {
    this.selectedConversation = conv;
    this.selectedTemplateName = '';
    this.templateParams = [];
    this.cerrarCrearContacto();
    this.loadMessages(conv.id);
    // Sólo pedimos el catálogo a Meta si de verdad hace falta.
    if (!this.isWindowOpen(conv)) {
      this.loadTemplates();
    }
  }

  loadMessages(conversationId: number): void {
    this.loadingMessages = true;
    this.errorMessage = null;
    this.conversationApi.listMessages(conversationId).subscribe({
      next: (list) => {
        this.applyMessages(list, true);
        this.loadingMessages = false;
      },
      error: (err) => {
        this.errorMessage = this.toErrorMessage(err, 'No se pudieron cargar los mensajes');
        this.loadingMessages = false;
      },
    });
  }

  /**
   * Vuelca la lista en la vista. `scrollToBottom` sólo va en la carga inicial: hacerlo en
   * cada tick del sondeo arrastraría al asesor al final justo cuando está leyendo atrás.
   */
  private applyMessages(list: WhatsappMessageResponse[] | null, scrollToBottom: boolean): void {
    // El backend devuelve DESC; lo invertimos para mostrar cronológico ASC en el render
    const ordered = (list || []).slice().sort((a, b) => {
      const ta = new Date(a.messageTimestamp || 0).getTime();
      const tb = new Date(b.messageTimestamp || 0).getTime();
      return ta - tb;
    });

    const hadMessages = this.messages.length;
    this.messages = ordered;
    this.prefetchMedia(ordered);

    // En el sondeo sólo se baja si llegó algo nuevo y el asesor ya estaba abajo, para no
    // interrumpirle la lectura.
    if (scrollToBottom || (ordered.length > hadMessages && this.isScrolledToBottom())) {
      this.scrollMessagesToBottom();
    }
  }

  // ===== Adjuntos recibidos y enviados =====

  /**
   * Baja los adjuntos que aún no estén en caché. Se hace aquí y no desde la plantilla
   * porque llamar a algo que dispara peticiones dentro del render acabaría pidiendo el
   * mismo archivo en cada ciclo de detección de cambios.
   */
  private prefetchMedia(messages: WhatsappMessageResponse[]): void {
    for (const message of messages) {
      if (!isWhatsappMediaMessage(message) || this.mediaUrls.has(message.id)) continue;
      // Se reserva el hueco antes de pedir para que dos ciclos seguidos no lancen dos
      // descargas del mismo adjunto.
      this.mediaUrls.set(message.id, '');
      this.messageApi.downloadMessageMedia(message.id).subscribe({
        next: (blob) => this.mediaUrls.set(message.id, URL.createObjectURL(blob)),
        error: () => {
          // Se deja el hueco vacío: la burbuja cae al texto "[Imagen]" en vez de quedarse
          // con un icono roto, y no se reintenta en bucle.
          this.mediaUrls.set(message.id, '');
        },
      });
    }
  }

  /** URL lista para pintar, o null mientras se descarga o si falló. */
  mediaUrlFor(message: WhatsappMessageResponse): string | null {
    const url = this.mediaUrls.get(message.id);
    return url ? url : null;
  }

  isMediaMessage(message: WhatsappMessageResponse): boolean {
    return isWhatsappMediaMessage(message);
  }

  /** Nombre de archivo para el enlace de descarga de un documento. */
  mediaFilename(message: WhatsappMessageResponse): string {
    const path = message.mediaPath || '';
    const slash = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
    const name = slash < 0 ? path : path.slice(slash + 1);
    return name || `adjunto-${message.id}`;
  }

  private isScrolledToBottom(): boolean {
    const el = this.scrollElement();
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  reloadAll(): void {
    this.loadPhones();
    if (this.selectedConversation) {
      this.loadMessages(this.selectedConversation.id);
    }
  }

  reloadConversations(): void {
    this.loadConversations();
    if (this.selectedConversation) {
      this.loadMessages(this.selectedConversation.id);
    }
  }

  isWindowOpen(conv: WhatsappConversation | null): boolean {
    if (!conv?.customerWindowUntil) return false;
    return new Date(conv.customerWindowUntil).getTime() > Date.now();
  }

  windowMessage(conv: WhatsappConversation | null): string {
    if (!conv) return '';
    if (this.isWindowOpen(conv)) {
      return `Ventana abierta hasta ${new Date(conv.customerWindowUntil!).toLocaleString()}`;
    }
    return 'Ventana de 24h cerrada — espera respuesta del cliente para volver a escribir.';
  }

  /** Hay algo que enviar en cuanto haya texto o adjunto: con adjunto el texto es opcional. */
  get canSend(): boolean {
    return (
      !!this.selectedConversation &&
      this.isWindowOpen(this.selectedConversation) &&
      !this.sending &&
      (this.composerText.trim().length > 0 || !!this.attachment)
    );
  }

  send(): void {
    if (!this.canSend) return;
    const conv = this.selectedConversation!;
    const text = this.composerText.trim();
    const file = this.attachment;

    this.sending = true;
    this.errorMessage = null;

    // Con adjunto hay dos pasos: primero sube a Meta, y sólo con el mediaId en mano se
    // envía. Si la subida falla no se manda nada, para no dejar un texto suelto que el
    // asesor creía que llevaba el archivo.
    const request$: Observable<SendMessageRequest> = file
      ? (() => {
          this.uploadingMedia = true;
          return this.messageApi.uploadMedia(conv.whatsappPhoneId, file).pipe(
            switchMap((res) => {
              this.uploadingMedia = false;
              return of({
                text: text || undefined,
                mediaId: res.mediaId,
                mediaType: this.mediaTypeOf(file),
                filename: file.name,
              } as SendMessageRequest);
            })
          );
        })()
      : of({ text } as SendMessageRequest);

    request$
      .pipe(switchMap((req) => this.messageApi.sendMessage(conv.id, req)))
      .subscribe({
        next: (msg) => {
          this.messages = [...this.messages, msg];
          this.prefetchMedia([msg]);
          this.composerText = '';
          this.clearAttachment();
          this.sending = false;
          this.uploadingMedia = false;
          this.scrollMessagesToBottom();
        },
        error: (err) => {
          this.sending = false;
          this.uploadingMedia = false;
          if (err instanceof HttpErrorResponse && err.status === 409) {
            this.errorMessage =
              'La ventana de 24h se cerró. No se puede enviar texto libre; usa una plantilla.';
          } else {
            this.errorMessage = this.toErrorMessage(err, 'No se pudo enviar el mensaje');
          }
        },
      });
  }

  // ===== Adjuntar al mensaje libre =====

  /**
   * Traduce el MIME del archivo al `type` que espera la Cloud API. Se manda explícito
   * porque Meta valida que el tipo declarado case con el archivo subido: mandar un PDF
   * como `image` lo rechaza.
   */
  private mediaTypeOf(file: File): WhatsappSendableMediaType {
    const mime = (file.type || '').toLowerCase();
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return 'document';
  }

  onAttachmentChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.length ? input.files[0] : null;
    this.clearAttachment();
    if (!file) return;
    this.attachment = file;
    // Vista previa local: se muestra antes de enviar para que el asesor confirme que
    // eligió el archivo correcto.
    if (file.type.startsWith('image/')) {
      this.attachmentPreviewUrl = URL.createObjectURL(file);
    }
  }

  clearAttachment(): void {
    if (this.attachmentPreviewUrl) {
      URL.revokeObjectURL(this.attachmentPreviewUrl);
      this.attachmentPreviewUrl = null;
    }
    this.attachment = null;
  }

  // ===== Iniciar conversación con un número que nunca escribió =====

  toggleStart(): void {
    this.startOpen = !this.startOpen;
    if (this.startOpen) {
      this.startPhoneId = this.selectedPhoneId ?? this.phones[0]?.id ?? null;
      this.resetTemplateForm();
      this.loadTemplates();
    }
  }

  get canStart(): boolean {
    return (
      !!this.startPhoneId &&
      this.startTo.replace(/\D/g, '').length >= 8 &&
      this.templateReady &&
      !this.sending
    );
  }

  startConversation(): void {
    if (!this.canStart) return;
    const t = this.selectedTemplate!;

    this.sending = true;
    this.errorMessage = null;

    const phoneId = Number(this.startPhoneId);
    this.resolveHeaderFields(phoneId)
      .pipe(
        switchMap((header) =>
          this.messageApi.startConversation({
            whatsappPhoneId: phoneId,
            to: this.startTo.trim(),
            templateName: t.name,
            language: t.language,
            params: this.templateParams.map((p) => p.trim()),
            ...header,
          })
        )
      )
      .subscribe({
        next: () => {
          this.sending = false;
          this.uploadingMedia = false;
          this.startOpen = false;
          this.startTo = '';
          this.resetTemplateForm();
          // La conversación es nueva: recargamos la lista para que aparezca.
          this.loadConversations();
        },
        error: (err) => {
          this.sending = false;
          this.uploadingMedia = false;
          this.errorMessage = this.toErrorMessage(err, 'No se pudo iniciar la conversación');
        },
      });
  }

  // ===== Plantillas: único envío posible con la ventana cerrada =====

  /** Se cargan una sola vez y en diferido: sólo hacen falta si la ventana está cerrada. */
  loadTemplates(): void {
    if (this.templatesLoaded || this.templatesLoading) return;
    // Un intento fallido no deja templatesLoaded en true, asi que "Reintentar" vuelve a entrar.
    this.templatesLoading = true;
    this.templatesError = null;
    this.messageApi.listTemplates().subscribe({
      next: (list) => {
        this.templates = list;
        this.templatesLoaded = true;
        this.templatesLoading = false;
      },
      error: (err) => {
        this.templatesLoading = false;
        this.templatesError = this.toErrorMessage(err, 'No se pudieron cargar las plantillas');
      },
    });
  }

  /**
   * Enviables. El listado trae tambien las PENDING y las REJECTED para que se vean en la
   * pantalla de plantillas, pero ofrecer una aqui solo produciria un error de Meta.
   */
  get approvedTemplates(): WhatsappTemplate[] {
    return this.templates.filter(isTemplateSendable);
  }

  onTemplateChange(): void {
    const t = this.approvedTemplates.find((x) => x.name === this.selectedTemplateName);
    this.templateParams = t ? Array(t.paramCount).fill('') : [];
    this.headerFile = null;
    this.headerParam = '';
  }

  get selectedTemplate(): WhatsappTemplate | null {
    return this.approvedTemplates.find((t) => t.name === this.selectedTemplateName) ?? null;
  }

  // ===== Cabecera de la plantilla =====
  //
  // Una plantilla con cabecera de imagen/vídeo/documento NO se puede enviar sin el
  // archivo: Meta la rechaza con el error 132012. El formato lo declara la propia
  // plantilla, así que aquí sólo se pide lo que esa plantilla exige.

  /** true si la plantilla elegida obliga a adjuntar un archivo. */
  get needsHeaderMedia(): boolean {
    return requiresMediaHeader(this.selectedTemplate?.headerFormat);
  }

  /** true si la cabecera es de texto y trae un {{1}} que hay que rellenar. */
  get needsHeaderText(): boolean {
    const t = this.selectedTemplate;
    return t?.headerFormat === 'TEXT' && t.headerParamCount > 0;
  }

  /** Etiqueta del adjunto según el formato que pide la plantilla. */
  get headerMediaLabel(): string {
    switch (this.selectedTemplate?.headerFormat) {
      case 'IMAGE':
        return 'Imagen de cabecera';
      case 'VIDEO':
        return 'Vídeo de cabecera';
      case 'DOCUMENT':
        return 'Documento de cabecera';
      default:
        return 'Archivo de cabecera';
    }
  }

  /** Filtra el diálogo de archivos a lo que Meta acepta para ese formato. */
  get headerMediaAccept(): string {
    switch (this.selectedTemplate?.headerFormat) {
      case 'IMAGE':
        return 'image/jpeg,image/png';
      case 'VIDEO':
        return 'video/mp4,video/3gpp';
      case 'DOCUMENT':
        return '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
      default:
        return '';
    }
  }

  onHeaderFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.headerFile = input.files?.length ? input.files[0] : null;
  }

  /**
   * Resuelve los campos de cabecera del envío. Cuando hay archivo lo sube primero a
   * Meta: el envío viaja con el mediaId, no con un enlace a nuestro storage, que los
   * servidores de Meta no alcanzarían en local.
   */
  private resolveHeaderFields(whatsappPhoneId: number): Observable<TemplateHeaderFields> {
    const t = this.selectedTemplate;
    if (!t?.headerFormat) {
      return of({});
    }

    if (this.needsHeaderText) {
      return of({ headerFormat: 'TEXT', headerText: this.headerParam.trim() });
    }

    if (this.needsHeaderMedia && this.headerFile) {
      const file = this.headerFile;
      this.uploadingMedia = true;
      return this.messageApi.uploadMedia(whatsappPhoneId, file).pipe(
        switchMap((res) => {
          this.uploadingMedia = false;
          return of({
            headerFormat: t.headerFormat,
            headerMediaId: res.mediaId,
            // Sólo lo usa DOCUMENT; en los demás formatos Meta lo ignora.
            headerFilename: file.name,
          } as TemplateHeaderFields);
        })
      );
    }

    // Cabecera fija (texto sin marcadores): no hay nada que parametrizar.
    return of({});
  }

  /** Vista previa con los {{n}} ya sustituidos, para que el agente vea qué va a mandar. */
  get templatePreview(): string {
    const t = this.selectedTemplate;
    if (!t?.bodyText) return '';
    return t.bodyText.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, idx) => {
      const value = this.templateParams[Number(idx) - 1];
      return value?.trim() ? value : match;
    });
  }

  get templateReady(): boolean {
    const t = this.selectedTemplate;
    if (!t) return false;
    const bodyOk = this.templateParams.length === t.paramCount
      && this.templateParams.every((p) => p.trim().length > 0);
    const headerOk =
      (!this.needsHeaderMedia || !!this.headerFile) &&
      (!this.needsHeaderText || this.headerParam.trim().length > 0);
    return bodyOk && headerOk;
  }

  sendTemplate(): void {
    const conv = this.selectedConversation;
    const t = this.selectedTemplate;
    if (!conv || !t || !this.templateReady) return;

    this.sending = true;
    this.errorMessage = null;

    this.resolveHeaderFields(conv.whatsappPhoneId)
      .pipe(
        switchMap((header) =>
          this.messageApi.sendTemplate(conv.id, {
            templateName: t.name,
            language: t.language,
            params: this.templateParams.map((p) => p.trim()),
            ...header,
          })
        )
      )
      .subscribe({
        next: (msg) => {
          this.messages = [...this.messages, msg];
          this.sending = false;
          this.uploadingMedia = false;
          this.resetTemplateForm();
          this.scrollMessagesToBottom();
        },
        error: (err) => {
          this.sending = false;
          this.uploadingMedia = false;
          this.errorMessage = this.toErrorMessage(err, 'No se pudo enviar la plantilla');
        },
      });
  }

  private resetTemplateForm(): void {
    this.selectedTemplateName = '';
    this.templateParams = [];
    this.headerFile = null;
    this.headerParam = '';
  }

  messageTypeLabel(type: string | null | undefined): string {
    return whatsappLabel(WHATSAPP_MESSAGE_TYPE_LABELS, type, 'Adjunto');
  }

  statusIcon(status: string): string {
    switch ((status || '').toUpperCase()) {
      case 'READ':
        return '✓✓';
      case 'DELIVERED':
        return '✓✓';
      case 'SENT':
        return '✓';
      case 'FAILED':
        return '!';
      case 'PENDING':
        return '…';
      default:
        return '';
    }
  }

  statusClass(status: string): string {
    switch ((status || '').toUpperCase()) {
      case 'READ':
        return 'text-sky-200';
      case 'FAILED':
        return 'text-red-300';
      default:
        return 'text-emerald-200';
    }
  }

  /**
   * Motivo del fallo tal cual lo devolvió Meta. Un "Fallido" a secas no le dice al asesor
   * si el número no está en la lista de permitidos, si la plantilla está pausada o si se
   * cerró la ventana — y esas tres se arreglan de formas distintas.
   */
  failureReason(message: WhatsappMessageResponse): string | null {
    if ((message.status || '').toUpperCase() !== 'FAILED') return null;
    return message.errorMessage?.trim() || 'Meta no devolvió un motivo.';
  }

  // ===== Ficha del contacto =====

  /**
   * Un hilo sin ficha es lo NORMAL desde que el alta automática viene apagada: el mensaje
   * llega a la bandeja igual, pero nadie creó el contacto. La pantalla no debe enlazar a
   * `/contactos/…` en ese caso — no hay id al que ir.
   */
  tieneContacto(c: WhatsappConversation | null): boolean {
    return !!c && c.contactId !== null && c.contactId !== undefined;
  }

  /**
   * Abre la ficha en el cajón lateral en vez de navegar: sacar al asesor del chat para ver
   * un dato del contacto le hace perder el hilo que estaba atendiendo.
   */
  abrirFicha(c: WhatsappConversation | null): void {
    if (!this.tieneContacto(c)) return;
    this.contactoDrawer.open({ contactoId: c!.contactId! });
  }

  abrirCrearContacto(): void {
    if (this.tieneContacto(this.selectedConversation)) return;
    this.crearContactoAbierto = true;
    this.nombreNuevoContacto = '';
  }

  cerrarCrearContacto(): void {
    this.crearContactoAbierto = false;
    this.nombreNuevoContacto = '';
  }

  /**
   * Crea la ficha del hilo abierto — o la vincula, si ese número ya tenía una guardada con
   * otro formato: el backend cruza por dígitos, así que «+51 987654321» y «51987654321»
   * son la misma persona. Las dos cosas se avisan distinto a propósito: «creado» y
   * «vinculado» no significan lo mismo para quien está mirando la cartera.
   */
  crearContacto(): void {
    const conv = this.selectedConversation;
    if (!conv || this.creandoContacto || this.tieneContacto(conv)) return;

    const nombre = this.nombreNuevoContacto.trim();
    this.creandoContacto = true;
    this.errorMessage = null;

    this.conversationApi
      .createContact(conv.id, nombre ? { fullname: nombre } : {})
      .subscribe({
        next: (res) => {
          this.creandoContacto = false;
          this.cerrarCrearContacto();
          // Se refresca el hilo para que traiga ya su contactId y su nombre: sin esto el
          // botón seguiría ofreciendo crear una ficha que ya existe.
          this.refrescarConversacion(conv.id);
          // El nombre que se anuncia es SIEMPRE el que devuelve el backend, nunca el
          // tecleado: con `created: false` la ficha ya existía y su nombre no se toca, así
          // que decir el del formulario haría creer que la ficha se llama de otra forma.
          const nombreFicha = res.contactName?.trim() || this.conversationPhone(conv);
          if (res.created) {
            this.toast.created(
              'Contacto',
              `Se creó la ficha de ${nombreFicha} y la conversación quedó vinculada a ella.`
            );
          } else {
            this.toast.success({
              title: 'Se vinculó el contacto que ya existía',
              message:
                `Ese número ya tenía ficha en el CRM (${nombreFicha}): la conversación quedó vinculada a ella y ` +
                'su nombre no se modificó. No se creó una ficha nueva.',
            });
          }
        },
        error: (err) => {
          this.creandoContacto = false;
          if (err instanceof HttpErrorResponse && err.status === 409) {
            // Alguien (u otro agente) ya le puso ficha mientras tanto: no es un error que
            // el asesor tenga que resolver, sólo hay que ponerle la pantalla al día.
            this.cerrarCrearContacto();
            this.refrescarConversacion(conv.id);
            this.toast.info({
              title: 'Ya tenía contacto',
              message: 'Esta conversación ya estaba vinculada a una ficha. Se actualizó la pantalla.',
            });
            return;
          }
          const msg = this.toErrorMessage(err, 'No se pudo crear el contacto');
          this.errorMessage = msg;
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  /**
   * Vuelve a leer UN hilo y lo reemplaza en memoria: el objeto seleccionado y su fila en la
   * lista.
   *
   * No se recarga el listado entero porque devuelve 50 hilos como mucho y no admite filtrar
   * por id: en un negocio con más conversaciones activas, la que está abierta puede caer
   * fuera de esa página y el asesor vería «Sin ficha» en el hilo cuya ficha acaba de crear.
   *
   * Si el refresco puntual falla se cae al listado completo: es peor —puede no traer este
   * hilo—, pero el fallback no puede ser dejar la pantalla afirmando algo que ya no es
   * cierto.
   */
  private refrescarConversacion(conversationId: number): void {
    this.conversationApi.get(conversationId).subscribe({
      next: (fresca) => this.aplicarConversacion(fresca),
      error: () => this.loadConversations(),
    });
  }

  /** Sustituye la conversación por la versión recién traída, sin tocar el resto. */
  private aplicarConversacion(fresca: WhatsappConversation): void {
    const indice = this.conversations.findIndex((c) => c.id === fresca.id);
    if (indice >= 0) {
      // Lista nueva en vez de mutar en sitio: el `track` del @for compara por id, pero los
      // getters que leen la lista sí necesitan ver una referencia distinta.
      const copia = this.conversations.slice();
      copia[indice] = fresca;
      this.conversations = copia;
    }
    if (this.selectedConversation?.id === fresca.id) {
      this.selectedConversation = fresca;
    }
    this.aplicarBusqueda();
  }

  /**
   * Cómo se llama el hilo: la persona antes que su número. Una bandeja de números a pelo
   * obliga a abrir cada uno para saber con quién se está hablando. Sin ficha —el caso
   * habitual con el alta automática apagada— cae al número, ya formateado.
   */
  conversationTitle(c: WhatsappConversation): string {
    const nombre = c.contactName?.trim();
    if (nombre) return nombre;
    return c.waId ? this.formatWaId(c.waId) : `Conversación ${c.id}`;
  }

  /** Número en formato de lectura, o cadena vacía si el hilo no lo tiene. */
  conversationPhone(c: WhatsappConversation): string {
    return c.waId ? this.formatWaId(c.waId) : '';
  }

  /**
   * `51987654321` → `+51 987 654 321`. Se agrupa de a tres desde la derecha: sin un
   * catálogo de prefijos no se puede aislar el código de país, y agrupar igual ya vuelve
   * legible una tira de 11-13 dígitos que, a pelo, hay que leer con el dedo.
   */
  private formatWaId(waId: string): string {
    const digitos = (waId || '').replace(/\D/g, '');
    if (!digitos) return '';
    const grupos: string[] = [];
    let resto = digitos;
    while (resto.length > 3) {
      grupos.unshift(resto.slice(-3));
      resto = resto.slice(0, -3);
    }
    grupos.unshift(resto);
    return `+${grupos.join(' ')}`;
  }

  /**
   * Baja el hilo al último mensaje.
   *
   * Se aplaza con `setTimeout` y no con `queueMicrotask`: el microtask corre ANTES de que
   * Angular pinte los mensajes nuevos, así que `scrollHeight` era el de antes y el hilo
   * quedaba a media altura. Antes no se notaba porque el contenedor no llegaba a hacer
   * scroll (le faltaba `min-h-0` y crecía por debajo del panel).
   */
  /**
   * Pide bajar el hilo al último mensaje. No se hace aquí: en este punto Angular todavía no
   * ha pintado los mensajes nuevos, así que `scrollHeight` es el de antes y el hilo se
   * quedaba arriba. Se marca y lo ejecuta `ngAfterViewChecked`, ya con el DOM al día.
   *
   * Antes no se notaba porque el contenedor ni siquiera llegaba a hacer scroll: le faltaba
   * `min-h-0` y crecía por debajo del panel en vez de desbordar dentro.
   */
  private scrollMessagesToBottom(): void {
    this.pendingScrollToBottom = true;
  }

  private scrollElement(): HTMLElement | null {
    return this.messagesScrollRef?.nativeElement ?? null;
  }

  private toErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body: any = err.error;
      if (typeof body === 'string') return body;
      if (body?.message) return body.message;
      if (body?.error) return body.error;
      return `${err.status} ${err.statusText || fallback}`;
    }
    return fallback;
  }
}
